import { invoke } from '~/lib/tauriInvoke'
import * as Sentry from '@sentry/nuxt'
import type { User } from '~/stores/user'
import { pendingPushNavigation } from '~/lib/push/pushNavigation'
import { clearComposerDrafts } from '~/lib/chat/composerDrafts'
import { useGroupsStore } from '~/stores/groups'
import { useGroupsSignalR } from '~/composables/useGroupsSignalR'
import { useUsersSignalR } from '~/composables/useUsersSignalR'
import { clearUserSession } from '~/lib/session/clearUserSession'

export interface AuthResponse {
  access_token: string
  expires_in: number
  refresh_expires_in: number
  refresh_token: string
  token_type: string
  id_token: string
  not_before_policy: number
  session_state: string
  scope: string
}

export interface InitiateAvatarUploadResponse {
  avatar_id: string
  upload_url: string
  expires_at?: string
}

export interface InitiateBannerUploadResponse {
  banner_id: string
  upload_url: string
  expires_at?: string
}

export interface UpdateUserRequest {
  display_name?: string | null
  description?: string | null
  status?: string | null
  preferred_language?: string | null
}

export function useAuth() {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  async function login(): Promise<AuthResponse> {
    isLoading.value = true
    error.value = null
    console.log('[Auth] login(): invoking start_oauth_login (opens system browser, waits for redirect)')
    try {
      const response = await invoke<AuthResponse>('start_oauth_login')
      console.log('[Auth] login(): start_oauth_login resolved')
      return response
    } catch (e: any) {
      console.error('[Auth] login(): start_oauth_login rejected:', e)
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'login' } })
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function cancelLogin() {
    try {
      await invoke('cancel_oauth_login')
    } catch (e: any) {
      Sentry.captureException(e, { extra: { action: 'cancel_login' } })
    } finally {
      isLoading.value = false
    }
  }

  // Failing here just means "not logged in" (no valid session yet) — the normal case on a fresh
  // launch, not a bug — so it deliberately isn't reported to Sentry.
  async function checkAuth() {
    try {
      return await invoke<boolean>('is_authenticated')
    } catch {
      return false
    }
  }

  /**
   * The one logout path for every button (sidebar, profile menu, dashboard, mobile settings):
   * leaves the call, ends the session, drops both SignalR sockets and every bit of this account's
   * loaded data (`clearUserSession`). Callers only navigate afterwards.
   */
  async function logout() {
    try {
      await unregisterPushToken()
      // Neither a queued navigation nor the "already handled" history belongs to the next user.
      pendingPushNavigation.clear()
      // Nor does text left unsent in the message boxes.
      clearComposerDrafts()
      // Leave a call while the session is still valid - releasing the channel lease is an
      // authenticated RPC. A failure here mustn't block the logout itself.
      const groupsStore = useGroupsStore()
      if (groupsStore.activeVoiceChannelId) await groupsStore.disconnectChannel().catch(() => {})
      await invoke('logout')
      useGroupsSignalR().disconnect()
      useUsersSignalR().disconnect()
      clearUserSession()
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'logout' } })
      throw e
    }
  }

  // Safe to call on every app start/foreground and after login - the backend upserts by
  // (user_id, device_token), and this no-ops on desktop (no FCM token file there). Failures are
  // swallowed: push notifications are a nice-to-have, never worth blocking auth over.
  async function registerPushToken() {
    try {
      console.debug('[Auth] Start refreshing push token')
      await invoke('register_push_token')
      console.debug('[Auth] registerPushToken succeeded')
    } catch (e: any) {
      console.warn('[Auth] registerPushToken failed:', e)
    }
  }

  async function unregisterPushToken() {
    try {
      await invoke('unregister_push_token')
    } catch (e: any) {
      console.warn('[Auth] unregisterPushToken failed:', e)
    }
  }

  async function refreshToken(): Promise<AuthResponse> {
    isLoading.value = true
    error.value = null
    try {
      const response = await invoke<AuthResponse>('refresh_token')
      console.log('[Debug] Refresh Token Response:', response)
      return response
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'refresh_token' } })
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function getMe(): Promise<User> {
    isLoading.value = true
    error.value = null
    try {
      return await invoke<User>('get_me')
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'get_me' } })
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function initiateAvatarUpload(contentType: string, sizeBytes: number): Promise<InitiateAvatarUploadResponse> {
    try {
      return await invoke<InitiateAvatarUploadResponse>('initiate_avatar_upload', {
        contentType,
        sizeBytes,
      })
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'initiate_avatar_upload' } })
      throw e
    }
  }

  async function updateUser(request: UpdateUserRequest): Promise<User> {
    try {
      return await invoke<User>('update_user', { request })
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'update_user' } })
      throw e
    }
  }

  async function confirmAvatarUpload(avatarId: string): Promise<User> {
    try {
      return await invoke<User>('confirm_avatar_upload', { avatarId })
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'confirm_avatar_upload' } })
      throw e
    }
  }

  async function deleteAvatar(): Promise<User> {
    try {
      return await invoke<User>('delete_avatar')
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'delete_avatar' } })
      throw e
    }
  }

  async function initiateBannerUpload(contentType: string, sizeBytes: number): Promise<InitiateBannerUploadResponse> {
    try {
      return await invoke<InitiateBannerUploadResponse>('initiate_banner_upload', {
        contentType,
        sizeBytes,
      })
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'initiate_banner_upload' } })
      throw e
    }
  }

  async function confirmBannerUpload(bannerId: string): Promise<User> {
    try {
      return await invoke<User>('confirm_banner_upload', { bannerId })
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'confirm_banner_upload' } })
      throw e
    }
  }

  async function deleteBanner(): Promise<User> {
    try {
      return await invoke<User>('delete_banner')
    } catch (e: any) {
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'delete_banner' } })
      throw e
    }
  }

  return {
    isLoading,
    error,
    login,
    cancelLogin,
    checkAuth,
    logout,
    refreshToken,
    getMe,
    updateUser,
    initiateAvatarUpload,
    confirmAvatarUpload,
    deleteAvatar,
    initiateBannerUpload,
    confirmBannerUpload,
    deleteBanner,
    registerPushToken,
    unregisterPushToken,
  }
}
